import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Text } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

const Carousel = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const [carouselWidth, setCarouselWidth] = useState(
    window.innerWidth < 501 ? window.innerWidth - 42 : 375
  );

  useEffect(() => {
    const handleResize = () => {
      setCarouselWidth(window.innerWidth < 501 ? window.innerWidth - 42 : 375);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (carouselRef.current) {
        const scrollLeft = carouselRef.current.scrollLeft;
        const itemWidth = carouselRef.current.children[0]?.offsetWidth || 0;
        const newIndex = Math.round(scrollLeft / itemWidth);
        setCurrentIndex(newIndex);
      }
    };

    const carouselNode = carouselRef.current;
    if (carouselNode) {
      carouselNode.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (carouselNode) {
        carouselNode.removeEventListener("scroll", handleScroll);
      }
    };
  }, [carouselRef]);

  const scrollToIndex = (index) => {
    if (carouselRef.current) {
      const item = carouselRef.current.children[index];
      if (item) {
        const itemWidth = item.offsetWidth;
        const centerOffset = (carouselWidth - itemWidth) / 2;
        carouselRef.current.scrollTo({
          left: index * itemWidth - centerOffset,
          behavior: "smooth",
        });
      }
    }
  };

  const nextSlide = () => {
    const newIndex = (currentIndex + 1) % items.length;
    scrollToIndex(newIndex);
  };

  const prevSlide = () => {
    const newIndex = (currentIndex - 1 + items.length) % items.length;
    scrollToIndex(newIndex);
  };

  return (
    <Box
      mb="0"
      width={`${carouselWidth}px`}
      position="relative"
      overflow="hidden"
    >
      <Box
        display="flex"
        ref={carouselRef}
        overflowX="auto"
        whiteSpace="nowrap"
        scrollBehavior="smooth"
        width="100%"
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {items.map((item, index) => (
          <Box
            key={index}
            display="inline-block"
            fontSize="14px"
            fontWeight="400"
            p="8px"
            borderRadius="6px"
            boxShadow={
              "box-shadow: rgba(255, 255, 255, 0.1) 0px 1px 1px 0px inset, rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px;"
            }
            minWidth={{
              base: items.length === 1 ? "auto" : "250px",
              sm: "300px",
              md: "300px",
            }}
            opacity={index === currentIndex ? 1 : 0.7}
            transform={index === currentIndex ? "scale(1)" : "scale(0.9)"}
            transition="transform 0.3s, opacity 0.3s"
          >
            <Text>{item.text}</Text>
          </Box>
        ))}
      </Box>
      {items.length > 1 && (
        <>
          <Button
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              width: "30px !important",
              transform: "translateY(-50%)",
              background: "#F5F5F5",
              borderRadius: "50%",
              padding: 0,
            }}
            onClick={nextSlide}
            size="md"
            zIndex={1}
            visibility={
              currentIndex === items.length - 1 ? "hidden" : "visible"
            }
          >
            <ChevronRightIcon boxSize={8} />
          </Button>
          <Button
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              width: "30px !important",
              transform: "translateY(-50%)",
              background: "#F5F5F5",
              borderRadius: "50%",
              padding: 0,
            }}
            onClick={prevSlide}
            size="md"
            zIndex={1}
            visibility={currentIndex <= 0 ? "hidden" : "visible"}
          >
            <ChevronLeftIcon boxSize={8} />
          </Button>
        </>
      )}
    </Box>
  );
};

export default Carousel;
